import type { Handler } from './handler';
import type { ApiFetchStoriesPort } from './ports/api-fetch-stories.port';
import { tryCatch } from '~/utils/apostatudo/try-catch';
import type { Story } from '~/stores/apostatudo/stories';
import { resolveAdapter } from '~/adapters/registry';

const CACHE_TTL_MS = 5 * 60 * 1000;

let cachedStories: Story[] | null = null;
let cachedAt = 0;
let inflight: Promise<Story[]> | null = null;

export const fetchStoriesHandler: Handler<Params, Ports, Result> = async (
  ports = {
    apiFetchStories: resolveAdapter('apiFetchStories'),
  },
) => {
  if (cachedStories && Date.now() - cachedAt < CACHE_TTL_MS) {
    return { stories: cachedStories };
  }

  if (inflight) {
    return { stories: await inflight };
  }

  inflight = (async () => {
    const [stories, fetchStoriesError] = await tryCatch(
      ports?.apiFetchStories.handle(),
    );

    if (fetchStoriesError) {
      console.error('Error fetching stories:', fetchStoriesError);

      return cachedStories ?? [];
    }

    // Only cache non-empty results so a transient empty response doesn't
    // mask stories for the full TTL window.
    if (stories && stories.length > 0) {
      cachedStories = stories;
      cachedAt = Date.now();

      return cachedStories;
    }

    return cachedStories ?? [];
  })();

  try {
    const result = await inflight;

    return { stories: result };
  } finally {
    inflight = null;
  }
};

type Params = undefined;

type Ports = { apiFetchStories: ApiFetchStoriesPort };

type Result = Promise<{ stories: Story[] }>;
